import React, { useMemo, useState } from 'react';
import moment from 'moment';
import {
  FaGift,
  FaBars,
  FaUsers,
  FaCalendarAlt,
  FaTimes,
  FaCheckCircle,
  FaTrash,
  FaSpinner,
  FaCog,
  FaPlus,
  FaEllipsisV,
  FaExclamationTriangle,
  FaSmile,
  FaChevronDown,
  FaBook,
  FaTshirt,
  FaTools,
  FaImage,
  FaCreditCard,
  FaUserPlus,
  FaInfoCircle,
  FaEdit,
  FaArrowLeft,
  FaFileAlt,
  FaUsersCog
} from 'react-icons/fa';
import { message, notification } from './feedback';

const iconMap = {
  gift: FaGift,
  bars: FaBars,
  team: FaUsers,
  calendar: FaCalendarAlt,
  close: FaTimes,
  'close-circle': FaTimes,
  'check-circle': FaCheckCircle,
  delete: FaTrash,
  loading: FaSpinner,
  setting: FaCog,
  plus: FaPlus,
  down: FaChevronDown,
  more: FaEllipsisV,
  warning: FaExclamationTriangle,
  smile: FaSmile,
  book: FaBook,
  skin: FaTshirt,
  tool: FaTools,
  picture: FaImage,
  copy: FaFileAlt,
  'credit-card': FaCreditCard,
  'user-add': FaUserPlus,
  'usergroup-add': FaUsersCog,
  'info-circle': FaInfoCircle,
  edit: FaEdit,
  left: FaArrowLeft,
  'file-add': FaFileAlt
};

export const Icon = ({ type, className, style, ...rest }) => {
  const Resolved = iconMap[type] || FaGift;
  return <Resolved className={className} style={style} {...rest} />;
};

export const Button = ({ children, className = '', style, onClick, type = 'button', disabled, ...rest }) => (
  <button type={type} className={`neumorphic-btn ${className}`.trim()} style={style} onClick={onClick} disabled={disabled} {...rest}>
    {children}
  </button>
);

export const Input = ({ className = '', style, onChange, value, defaultValue, placeholder, type = 'text', ...rest }) => (
  <input
    className={`neumorphic-input ${className}`.trim()}
    style={style}
    onChange={onChange}
    value={value}
    defaultValue={defaultValue}
    placeholder={placeholder}
    type={type}
    {...rest}
  />
);

Input.TextArea = ({ className = '', style, onChange, value, defaultValue, placeholder, rows = 4, autosize, ...rest }) => (
  <textarea
    className={`neumorphic-input ${className}`.trim()}
    style={style}
    onChange={onChange}
    value={value}
    defaultValue={defaultValue}
    placeholder={placeholder}
    rows={rows}
    {...rest}
  />
);

export const InputNumber = ({ className = '', style, onChange, value, defaultValue, min, max, parser, formatter, ...rest }) => {
  const hasExplicitValue = value !== undefined;
  const resolvedValue = value === undefined ? defaultValue : value;
  return (
    <input
      className={`neumorphic-input ${className}`.trim()}
      style={style}
      type="number"
      min={min}
      max={max}
      {...(hasExplicitValue ? { value: resolvedValue === undefined || resolvedValue === null ? '' : resolvedValue } : { defaultValue: resolvedValue === undefined || resolvedValue === null ? undefined : resolvedValue })}
      onChange={(event) => {
        const raw = event.target.value;
        const parsedRaw = parser ? parser(raw) : raw;
        const parsed = parsedRaw === '' || parsedRaw === undefined || parsedRaw === null ? undefined : Number(parsedRaw);
        if (onChange) {
          onChange(parsed);
        }
      }}
      {...rest}
    />
  );
};

export const Checkbox = ({ checked, defaultChecked, onChange, style, children, ...rest }) => {
  const resolvedChecked = checked === undefined ? defaultChecked : checked;
  const checkbox = <input style={style} type="checkbox" checked={resolvedChecked} onChange={onChange} {...rest} />;
  if (children === undefined || children === null) {
    return checkbox;
  }
  return (
    <label style={{ display: 'inline-flex', gap: '6px', alignItems: 'center' }}>
      {checkbox}
      <span>{children}</span>
    </label>
  );
};

Checkbox.Group = ({ options = [], value = [], onChange }) => {
  const selectedValues = Array.isArray(value) ? value : [];
  return (
    <div style={{ display: 'grid', gap: '6px', textAlign: 'left' }}>
      {options.map((option) => {
        const optionValue = typeof option === 'string' ? option : option.value;
        const optionLabel = typeof option === 'string' ? option : option.label;
        const checked = selectedValues.includes(optionValue);
        return (
          <label key={optionValue} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <input
              type="checkbox"
              checked={checked}
              onChange={(event) => {
                let next = selectedValues;
                if (event.target.checked) {
                  next = [...selectedValues, optionValue];
                } else {
                  next = selectedValues.filter((item) => item !== optionValue);
                }
                if (onChange) {
                  onChange(next);
                }
              }}
            />
            <span>{optionLabel}</span>
          </label>
        );
      })}
    </div>
  );
};

export const Tooltip = ({ title, children }) => (
  <span title={title}>
    {children}
  </span>
);

const RadioContext = React.createContext({
  value: undefined,
  onChange: undefined,
  name: undefined
});

export const Radio = ({ children, value, style }) => {
  const context = React.useContext(RadioContext);
  return (
    <label style={style}>
      <input
        type="radio"
        value={value}
        checked={String(context.value) === String(value)}
        name={context.name}
        onChange={(event) => {
          if (context.onChange) {
            context.onChange(event);
          }
        }}
      />
      {children}
    </label>
  );
};

Radio.Group = ({ children, onChange, defaultValue, value }) => {
  const name = useMemo(() => `radio-${Math.random().toString(36).slice(2)}`, []);
  const [internalValue, setInternalValue] = useState(defaultValue);
  const resolvedValue = value === undefined ? internalValue : value;
  return (
    <RadioContext.Provider
      value={{
        name,
        value: resolvedValue,
        onChange: (event) => {
          setInternalValue(event.target.value);
          if (onChange) {
            onChange(event);
          }
        }
      }}
    >
      <div>{children}</div>
    </RadioContext.Provider>
  );
};

const SelectContext = React.createContext(false);

export const Select = ({ children, style, onChange, value, defaultValue, id, ...rest }) => {
  const resolvedValue = value === undefined ? defaultValue : value;
  const safeValue = resolvedValue === undefined || resolvedValue === null || typeof resolvedValue === 'object'
    ? ''
    : String(resolvedValue);
  return (
    <select
      className="neumorphic-select"
      id={id}
      style={style}
      value={safeValue}
      onChange={(event) => {
        if (onChange) {
          onChange(event.target.value);
        }
      }}
      {...rest}
    >
      <SelectContext.Provider value>{children}</SelectContext.Provider>
    </select>
  );
};

export const Option = ({ value, children }) => {
  const optionValue = value === undefined || value === null || typeof value === 'object' ? '' : String(value);
  return <option value={optionValue}>{typeof children === 'string' ? children : (optionValue || 'Option')}</option>;
};

Select.Option = Option;

export const Switch = ({ onChange, defaultChecked, checkedChildren, unCheckedChildren }) => {
  const [checked, setChecked] = useState(!!defaultChecked);
  return (
    <label className="neumorphic-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => {
          const next = event.target.checked;
          setChecked(next);
          if (onChange) {
            onChange(next);
          }
        }}
      />
      <span>{checked ? checkedChildren : unCheckedChildren}</span>
    </label>
  );
};

export const Layout = ({ children, className = '', style, ...rest }) => <div className={className} style={style} {...rest}>{children}</div>;
Layout.Header = ({ children, className = '', style, ...rest }) => <header className={className} style={style} {...rest}>{children}</header>;
Layout.Sider = ({ children, className = '', style, ...rest }) => <aside className={className} style={style} {...rest}>{children}</aside>;

export const Drawer = ({ visible, onClose, children, placement = 'right' }) => {
  if (!visible) {
    return null;
  }
  return (
    <div className="settings-drawer-overlay" onClick={onClose}>
      <div className={`settings-drawer drawer-${placement}`} onClick={(event) => event.stopPropagation()}>
        <button type="button" className="neumorphic-btn" onClick={onClose}>
          Close
        </button>
        {children}
      </div>
    </div>
  );
};

export const Affix = ({ children, offsetTop = 0, offset = 0 }) => {
  const top = typeof offsetTop === 'number' ? offsetTop : offset;
  return <div style={{ position: 'sticky', top, zIndex: 1000 }}>{children}</div>;
};

export const Menu = ({ children, className = '', style, onClick }) => {
  return (
    <nav className={className} style={{ display: 'flex', gap: '8px', ...style }}>
      {React.Children.map(children, (child, index) => {
        if (!React.isValidElement(child)) {
          return child;
        }
        const existingOnClick = child.props.onClick;
        const itemKey = child.key || `item_${index}`;
        return React.cloneElement(child, {
          onClick: (event) => {
            if (onClick) {
              onClick({ key: String(itemKey), domEvent: event });
            }
            if (existingOnClick) {
              existingOnClick(event);
            }
          }
        });
      })}
    </nav>
  );
};

Menu.Item = ({ children, onClick }) => (
  <button type="button" className="topBarMenuItem neumorphic-btn" onClick={onClick}>
    {children}
  </button>
);

export const Dropdown = ({ overlay, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <span onClick={() => setOpen((value) => !value)}>{children}</span>
      {open ? (
        <div className="neumorphic-card" style={{ position: 'absolute', zIndex: 1005, top: '100%', right: 0 }} onClick={() => setOpen(false)}>
          {overlay}
        </div>
      ) : null}
    </div>
  );
};

export const Popconfirm = ({ title, onConfirm, children }) => {
  const child = React.Children.only(children);
  return React.cloneElement(child, {
    onClick: (event) => {
      const proceed = window.confirm(title || 'Are you sure?');
      if (proceed && onConfirm) {
        onConfirm();
      }
      if (child.props.onClick) {
        child.props.onClick(event);
      }
    }
  });
};

export const Card = ({ children, className = '', style, onClick, actions, actionsStyle, actionsClassName = '', cover, bodyStyle, ...rest }) => {
  return (
    <section className={`neumorphic-card ${className}`.trim()} style={style} onClick={onClick} {...rest}>
      {cover ? <div>{cover}</div> : null}
      <div style={bodyStyle}>{children}</div>
      {Array.isArray(actions) && actions.length ? (
        <div
          className={`ui-card-actions ${actionsClassName}`.trim()}
          style={{ marginTop: '10px', display: 'flex', justifyContent: 'space-around', gap: '8px', ...actionsStyle }}
        >
          {actions}
        </div>
      ) : null}
    </section>
  );
};

Card.Meta = ({ title, description }) => (
  <div style={{ marginTop: '8px' }}>
    <div style={{ fontWeight: 700 }}>{title}</div>
    <div style={{ opacity: 0.85 }}>{description}</div>
  </div>
);

export const List = ({ dataSource, renderItem, children, className = '', style, header, footer, bordered, grid }) => {
  const gridStyle = grid
    ? {
      display: 'grid',
      gap: `${grid.gutter || 12}px`,
      gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))'
    }
    : {};
  if (Array.isArray(dataSource) && renderItem) {
    return (
      <ul className={className} style={{ listStyle: 'none', margin: 0, padding: 0, ...(bordered ? { border: '1px solid rgba(0,0,0,0.12)', borderRadius: '14px' } : {}), ...gridStyle, ...style }}>
        {header ? <li style={{ gridColumn: grid ? '1 / -1' : undefined }}>{header}</li> : null}
        {dataSource.map((item, index) => renderItem(item, index))}
        {footer ? <li style={{ gridColumn: grid ? '1 / -1' : undefined }}>{footer}</li> : null}
      </ul>
    );
  }
  return (
    <ul className={className} style={{ listStyle: 'none', margin: 0, padding: 0, ...(bordered ? { border: '1px solid rgba(0,0,0,0.12)', borderRadius: '14px' } : {}), ...gridStyle, ...style }}>
      {header ? <li style={{ gridColumn: grid ? '1 / -1' : undefined }}>{header}</li> : null}
      {children}
      {footer ? <li style={{ gridColumn: grid ? '1 / -1' : undefined }}>{footer}</li> : null}
    </ul>
  );
};

List.Item = ({ children, className = '', style }) => (
  <li className={className} style={style}>
    {children}
  </li>
);

const getMonthDates = (activeMoment) => {
  const start = activeMoment.clone().startOf('month').startOf('week');
  const end = activeMoment.clone().endOf('month').endOf('week');
  const dates = [];
  const cursor = start.clone();
  while (cursor.isSameOrBefore(end, 'day')) {
    dates.push(cursor.clone());
    cursor.add(1, 'day');
  }
  return dates;
};

export const Calendar = ({ dateCellRender, monthCellRender }) => {
  const [active, setActive] = useState(moment());
  const dates = getMonthDates(active);
  const monthSummary = monthCellRender ? monthCellRender(active.clone()) : null;

  return (
    <section className="neumorphic-card" style={{ marginTop: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
        <button type="button" className="neumorphic-btn" onClick={() => setActive((value) => value.clone().subtract(1, 'month'))}>
          Prev
        </button>
        <strong>{active.format('MMMM YYYY')}</strong>
        <button type="button" className="neumorphic-btn" onClick={() => setActive((value) => value.clone().add(1, 'month'))}>
          Next
        </button>
      </div>
      {monthSummary}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: '8px' }}>
        {dates.map((date) => (
          <div key={date.format('YYYY-MM-DD')} className="neumorphic-panel" style={{ minHeight: '94px', textAlign: 'left' }}>
            <div style={{ fontWeight: 700, marginBottom: '6px' }}>{date.date()}</div>
            <div>{dateCellRender ? dateCellRender(date) : null}</div>
          </div>
        ))}
      </div>
    </section>
  );
};

export const DatePicker = ({ onChange, defaultValue }) => {
  const [value, setValue] = useState(defaultValue ? moment(defaultValue).format('YYYY-MM-DD') : '');
  return (
    <input
      className="neumorphic-input"
      type="date"
      value={value}
      onChange={(event) => {
        const raw = event.target.value;
        setValue(raw);
        if (onChange) {
          onChange(raw ? moment(raw, 'YYYY-MM-DD') : null);
        }
      }}
    />
  );
};

export const Table = ({ dataSource = [], columns = [] }) => {
  return (
    <div className="neumorphic-card" style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.key || column.dataIndex} style={{ textAlign: 'left', padding: '8px' }}>
                {column.title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {dataSource.map((row) => (
            <tr key={row.key}>
              {columns.map((column) => (
                <td key={`${row.key}-${column.key || column.dataIndex}`} style={{ padding: '8px' }}>
                  {row[column.dataIndex]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const ConfigProvider = ({ children }) => children;

notification.open = ({ message: msg, description, duration }) => {
  notification.info({ message: msg, description, duration });
};

export const Row = ({ children, style, gutter = 0 }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', marginLeft: gutter ? -gutter / 2 : undefined, marginRight: gutter ? -gutter / 2 : undefined, ...style }}>{children}</div>
);

export const Col = ({ children, style, span = 24, lg, md, sm, xs }) => {
  const resolved = lg || md || sm || xs || span;
  const width = `${(resolved / 24) * 100}%`;
  return (
    <div style={{ flex: `0 0 ${width}`, maxWidth: width, ...style }}>
      {children}
    </div>
  );
};

export { message, notification };
